import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Colors } from 'context';
import { SkillIcon } from 'atoms';
import { LessonSkill } from 'components/types/lesson';
import fontService, { FontWeight } from 'services/FontService';

interface ILessonSkills {
  skills: LessonSkill[];
  /** Занятие прошло → навык уже у студента. Признак с сервера, не по дате. */
  granted?: boolean;
}

/**
 * Что даёт занятие — на самом занятии (ТЗ 0040, доводка).
 *
 * Владелец: «пусть для каждого занятия студенты видят навыки, которые они
 * приобретут сейчас». До этого механизм работал молча: навык появлялся в
 * «Моём росте» задним числом, а на занятии про него не было ни слова —
 * студент учился в одном месте, а результат видел в другом.
 *
 * Две формулировки, а не одна с галочкой: «получите» — это обещание, ради
 * которого стоит прийти, «получили» — уже итог. Одна нейтральная фраза не
 * работает ни там, ни там.
 */
export const LessonSkills = memo(({ skills, granted }: ILessonSkills) => {
  const { t } = useTranslation();

  // Занятие без навыков — обычное дело: молчим, а не показываем пустую плашку.
  if (!skills.length) return null;

  return (
    <Wrapper $granted={Boolean(granted)}>
      <Title>{granted ? t('lesson.skillsGot') : t('lesson.skillsWillGet')}</Title>
      <List>
        {skills.map((skill) => (
          <Chip key={skill.id}>
            <SkillIcon
              slug={skill.slug}
              name={skill.name}
              category={skill.category}
              iconUrl={skill.iconUrl}
              size={18}
            />
            <span>{skill.name}</span>
          </Chip>
        ))}
      </List>
      <Note>{granted ? t('lesson.skillsGotNote') : t('lesson.skillsWillGetNote')}</Note>
    </Wrapper>
  );
});

const Wrapper = styled.div<{ $granted: boolean }>`
  margin-bottom: 24px;
  padding: 16px 20px;
  border-radius: 12px;
  border: 1px solid ${({ $granted }) => ($granted ? Colors.signal_green : Colors.system_gray)};
  background: ${Colors.bg_gray};
`;

const Title = styled.div`
  ${fontService.text({ size: 14, weight: FontWeight.bold })};
  color: ${Colors.black};
  margin-bottom: 10px;
`;

const List = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  background: ${Colors.white};
  border: 1px solid ${Colors.system_gray};
  ${fontService.text({ size: 13 })};
  color: ${Colors.black};
`;

const Note = styled.div`
  margin-top: 10px;
  ${fontService.text({ size: 12 })};
  color: ${Colors.gray_dark};
`;
